/* ============================================
   NuevoContacto — Alta de contacto por pasos (vista completa)
   Paso 1: Datos del contacto
   Paso 2: Empresas afiliadas (con roles)
   Paso 3: Confirmar y crear
   Mismo patron visual que NuevoPartner.
   ============================================ */

import { useState, useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useApi } from '@/hooks/useApi'
import { showToast } from '@/components/layout/Toast'
import type { Cliente, RolContacto } from '@/lib/types'
import { ROLES_CONTACTO } from '@/lib/types'
import { PageHeader } from '@/components/ui'

const STEPS = ['Datos', 'Empresas', 'Confirmar']

interface AfilForm {
  cliente_id: number | null
  roles: RolContacto[]
  cargo_empresa: string
  es_principal: boolean
}

const emptyAfil = (): AfilForm => ({ cliente_id: null, roles: [], cargo_empresa: '', es_principal: false })

export default function NuevoContacto() {
  const api = useApi()
  const navigate = useNavigate()
  // La pagina se monta en el ERP y en el espacio partner.
  const base = useLocation().pathname.startsWith('/partner') ? '/partner/contactos' : '/clientes/contactos'

  const [step, setStep] = useState(0)
  const [saving, setSaving] = useState(false)

  const [form, setForm] = useState({
    nombre: '', apellido: '', email: '', telefono: '', cargo: '', notas: '',
  })
  const [afiliaciones, setAfiliaciones] = useState<AfilForm[]>([emptyAfil()])
  const [clientes, setClientes] = useState<Cliente[]>([])

  useEffect(() => {
    api.get<Cliente[]>('/api/clientes')
      .then(setClientes)
      .catch(() => showToast('No se pudieron cargar las empresas', 'error'))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const afilsValidas = afiliaciones.filter(a => a.cliente_id !== null)

  // ─── Validacion por paso ───
  function validarDatos(): boolean {
    if (!form.nombre.trim()) { showToast('El nombre es requerido', 'error'); return false }
    if (form.email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) {
      showToast('El email no tiene un formato valido', 'error'); return false
    }
    return true
  }

  function validarEmpresas(): boolean {
    for (const a of afilsValidas) {
      if (a.roles.length === 0) {
        const emp = clientes.find(c => c.id === a.cliente_id)?.empresa ?? ''
        showToast(`Selecciona al menos un rol para "${emp}"`, 'error')
        return false
      }
    }
    return true
  }

  function siguiente() {
    if (step === 0 && !validarDatos()) return
    if (step === 1 && !validarEmpresas()) return
    setStep(s => Math.min(s + 1, STEPS.length - 1))
  }

  function atras() { setStep(s => Math.max(s - 1, 0)) }

  // ─── Afiliaciones ───
  function setAfil(i: number, patch: Partial<AfilForm>) {
    setAfiliaciones(prev => prev.map((a, idx) => idx === i ? { ...a, ...patch } : a))
  }

  function toggleRol(i: number, rol: RolContacto) {
    setAfiliaciones(prev => prev.map((a, idx) => {
      if (idx !== i) return a
      const tiene = a.roles.includes(rol)
      return { ...a, roles: tiene ? a.roles.filter(r => r !== rol) : [...a.roles, rol] }
    }))
  }

  async function crear() {
    if (!validarDatos() || !validarEmpresas()) return
    setSaving(true)
    try {
      const creado = await api.post<{ id: number }>('/api/contactos', {
        nombre: form.nombre.trim(),
        apellido: form.apellido.trim() || null,
        email: form.email.trim() || null,
        telefono: form.telefono.trim() || null,
        cargo: form.cargo.trim() || null,
        notas: form.notas.trim() || null,
      })

      for (const af of afilsValidas) {
        await api.post(`/api/contactos/${creado.id}/empresas`, {
          cliente_id: af.cliente_id!,
          roles: af.roles,
          cargo_empresa: af.cargo_empresa || null,
          es_principal: af.es_principal,
        })
      }

      showToast('Contacto creado', 'success')
      navigate(base)
    } catch {
      showToast('Error al crear contacto', 'error')
    } finally {
      setSaving(false)
    }
  }

  const nombreEmpresa = (id: number | null) => clientes.find(c => c.id === id)?.empresa ?? '—'

  return (
    <div className="page-enter" style={{ maxWidth: 780, margin: '0 auto' }}>
      {/* Header */}
      <PageHeader breadcrumbs={[{ label: 'Contactos', to: base }, { label: 'Nuevo' }]} />

      {/* Stepper */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 0, marginBottom: 24 }}>
        {STEPS.map((label, i) => (
          <div key={label} style={{ display: 'flex', alignItems: 'center', flex: i < STEPS.length - 1 ? 1 : '0 0 auto' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{
                width: 28, height: 28, borderRadius: '50%', display: 'grid', placeItems: 'center',
                fontSize: '0.82rem', fontWeight: 700, flexShrink: 0,
                background: i <= step ? 'var(--primary-light)' : 'var(--bg-card-hover)',
                color: i <= step ? 'var(--text-on-primary)' : 'var(--text-muted)',
                border: i === step ? '2px solid var(--primary-light)' : '2px solid transparent',
              }}>{i < step ? '✓' : i + 1}</span>
              <span style={{ fontSize: '0.85rem', fontWeight: 600, color: i <= step ? 'var(--text-primary)' : 'var(--text-muted)' }}>{label}</span>
            </div>
            {i < STEPS.length - 1 && (
              <div style={{ flex: 1, height: 2, margin: '0 12px', background: i < step ? 'var(--primary-light)' : 'var(--border)' }} />
            )}
          </div>
        ))}
      </div>

      {/* Contenido del paso */}
      <div style={{ border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: '1.5rem', background: 'var(--bg-card)', marginBottom: 20 }}>

        {/* PASO 1 — Datos */}
        {step === 0 && (
          <div>
            <h3 style={{ margin: '0 0 4px', fontSize: '1.05rem' }}>¿Quien es el contacto?</h3>
            <p style={{ margin: '0 0 16px', color: 'var(--text-secondary)', fontSize: '0.88rem' }}>
              El email es opcional, pero hace falta si mas adelante quieres darle acceso al portal.
            </p>

            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Nombre *</label>
                <input className="form-input" value={form.nombre} autoFocus
                  onChange={e => setForm(f => ({ ...f, nombre: e.target.value }))} />
              </div>
              <div className="form-group">
                <label className="form-label">Apellido</label>
                <input className="form-input" value={form.apellido}
                  onChange={e => setForm(f => ({ ...f, apellido: e.target.value }))} />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Email</label>
                <input className="form-input" type="email" value={form.email}
                  onChange={e => setForm(f => ({ ...f, email: e.target.value }))} />
              </div>
              <div className="form-group">
                <label className="form-label">Teléfono</label>
                <input className="form-input" value={form.telefono}
                  onChange={e => setForm(f => ({ ...f, telefono: e.target.value }))} />
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">Cargo</label>
              <input className="form-input" placeholder="Ej: CTO, Gerente Comercial" value={form.cargo}
                onChange={e => setForm(f => ({ ...f, cargo: e.target.value }))} />
            </div>

            <div className="form-group">
              <label className="form-label">Notas</label>
              <textarea className="form-input" rows={3} placeholder="Notas internas sobre este contacto..."
                value={form.notas} onChange={e => setForm(f => ({ ...f, notas: e.target.value }))} />
            </div>
          </div>
        )}

        {/* PASO 2 — Empresas */}
        {step === 1 && (
          <div>
            <h3 style={{ margin: '0 0 4px', fontSize: '1.05rem' }}>¿A que empresas pertenece?</h3>
            <p style={{ margin: '0 0 16px', color: 'var(--text-secondary)', fontSize: '0.88rem' }}>
              Puedes dejarlo vacio y vincularlo despues. Si eliges una empresa, marca al menos un rol.
            </p>

            {afiliaciones.map((af, i) => (
              <div key={i} style={{ border: '1px solid var(--border)', borderRadius: 8, padding: '1rem', marginBottom: 12 }}>
                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">Empresa</label>
                    <select className="form-input" value={af.cliente_id ?? ''}
                      onChange={e => setAfil(i, { cliente_id: e.target.value ? Number(e.target.value) : null })}>
                      <option value="">— Seleccionar empresa —</option>
                      {clientes.map(c => <option key={c.id} value={c.id}>{c.empresa}</option>)}
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Cargo en esta empresa</label>
                    <input className="form-input" placeholder="Ej: Director Técnico" value={af.cargo_empresa}
                      onChange={e => setAfil(i, { cargo_empresa: e.target.value })} />
                  </div>
                </div>

                <div className="form-group">
                  <label className="form-label">Roles *</label>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                    {ROLES_CONTACTO.map(rol => {
                      const activo = af.roles.includes(rol)
                      return (
                        <button key={rol} type="button" onClick={() => toggleRol(i, rol)} style={{
                          padding: '5px 10px', borderRadius: 999, cursor: 'pointer', fontSize: '0.74rem', fontWeight: 600,
                          border: activo ? '1px solid var(--primary-light)' : '1px solid var(--border)',
                          background: activo ? 'color-mix(in srgb, var(--primary-light) 15%, transparent)' : 'transparent',
                          color: activo ? 'var(--text-primary)' : 'var(--text-secondary)',
                        }}>{rol}</button>
                      )
                    })}
                  </div>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.82rem', cursor: 'pointer' }}>
                    <input type="checkbox" checked={af.es_principal}
                      onChange={e => setAfil(i, { es_principal: e.target.checked })} />
                    Contacto principal de esta empresa
                  </label>
                  {afiliaciones.length > 1 && (
                    <button type="button" className="btn btn-sm btn-danger"
                      onClick={() => setAfiliaciones(prev => prev.filter((_, idx) => idx !== i))}>
                      Quitar
                    </button>
                  )}
                </div>
              </div>
            ))}

            <button type="button" className="btn btn-sm btn-secondary"
              onClick={() => setAfiliaciones(prev => [...prev, emptyAfil()])}>
              + Agregar empresa
            </button>
          </div>
        )}

        {/* PASO 3 — Confirmar */}
        {step === 2 && (
          <div>
            <h3 style={{ margin: '0 0 4px', fontSize: '1.05rem' }}>Revisa antes de crear</h3>
            <p style={{ margin: '0 0 16px', color: 'var(--text-secondary)', fontSize: '0.88rem' }}>
              Podras editar cualquiera de estos datos despues.
            </p>

            <dl style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '8px 16px', margin: 0, fontSize: '0.88rem' }}>
              <dt style={{ color: 'var(--text-secondary)' }}>Nombre</dt>
              <dd style={{ margin: 0, fontWeight: 600 }}>{form.nombre} {form.apellido}</dd>
              <dt style={{ color: 'var(--text-secondary)' }}>Email</dt>
              <dd style={{ margin: 0 }}>{form.email || '—'}</dd>
              <dt style={{ color: 'var(--text-secondary)' }}>Teléfono</dt>
              <dd style={{ margin: 0 }}>{form.telefono || '—'}</dd>
              <dt style={{ color: 'var(--text-secondary)' }}>Cargo</dt>
              <dd style={{ margin: 0 }}>{form.cargo || '—'}</dd>
              <dt style={{ color: 'var(--text-secondary)' }}>Empresas</dt>
              <dd style={{ margin: 0 }}>
                {afilsValidas.length === 0 ? 'Ninguna' : (
                  <ul style={{ margin: 0, paddingLeft: 16 }}>
                    {afilsValidas.map((a, i) => (
                      <li key={i}>
                        <strong>{nombreEmpresa(a.cliente_id)}</strong>
                        {' — '}{a.roles.join(', ')}
                        {a.es_principal && ' (principal)'}
                      </li>
                    ))}
                  </ul>
                )}
              </dd>
            </dl>

            {!form.email && (
              <p style={{ marginTop: 16, fontSize: '0.82rem', color: 'var(--text-muted)' }}>
                Sin email no podras crearle acceso al portal mas adelante.
              </p>
            )}
          </div>
        )}
      </div>

      {/* Navegación */}
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
        <button className="btn btn-secondary" onClick={step === 0 ? () => navigate(base) : atras} disabled={saving}>
          {step === 0 ? 'Cancelar' : 'Atrás'}
        </button>
        {step < STEPS.length - 1 ? (
          <button className="btn btn-primary" onClick={siguiente}>Siguiente</button>
        ) : (
          <button className="btn btn-primary" onClick={crear} disabled={saving}>
            {saving ? 'Creando...' : 'Crear contacto'}
          </button>
        )}
      </div>
    </div>
  )
}
